import type { SessionListDirectory, SessionListItem } from '../features/sessions/sessionList';

const STORAGE_KEY_PREFIX = 'paddiq-session-list-v3';
export const SESSION_LIST_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export type SessionListCachePayload = {
  savedAt: number;
  data?: SessionListItem[];
  directory?: SessionListDirectory | null;
};

const memoryCache = new Map<string, SessionListCachePayload>();

function storageKey(teamId: string) {
  return `${STORAGE_KEY_PREFIX}:${teamId}`;
}

function readStorage(teamId: string): SessionListCachePayload | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const raw = window.localStorage.getItem(storageKey(teamId));
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as SessionListCachePayload;
    if (
      !parsed?.savedAt ||
      (!Array.isArray(parsed.data) && !Array.isArray(parsed.directory?.sessions))
    ) {
      return null;
    }

    if (Date.now() - parsed.savedAt > SESSION_LIST_CACHE_MAX_AGE_MS) {
      window.localStorage.removeItem(storageKey(teamId));
      return null;
    }

    return parsed;
  } catch {
    window.localStorage.removeItem(storageKey(teamId));
    return null;
  }
}

export function readSessionListCacheSync(teamId: string): SessionListCachePayload | null {
  const fromMemory = memoryCache.get(teamId);
  if (fromMemory && Date.now() - fromMemory.savedAt <= SESSION_LIST_CACHE_MAX_AGE_MS) {
    return fromMemory;
  }

  const fromStorage = readStorage(teamId);
  if (fromStorage) {
    memoryCache.set(teamId, fromStorage);
    return fromStorage;
  }

  return null;
}

export function writeSessionListCache(teamId: string, directory: SessionListDirectory) {
  const payload: SessionListCachePayload = {
    savedAt: Date.now(),
    directory,
  };

  memoryCache.set(teamId, payload);

  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(storageKey(teamId), JSON.stringify(payload));
  } catch {
    // Ignore quota errors — memory cache still helps for this session.
  }
}

export function invalidateSessionListCache(teamId?: string) {
  if (teamId) {
    memoryCache.delete(teamId);
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(storageKey(teamId));
    }
    return;
  }

  memoryCache.clear();
  if (typeof window === 'undefined') {
    return;
  }

  for (let index = window.localStorage.length - 1; index >= 0; index -= 1) {
    const key = window.localStorage.key(index);
    if (key?.startsWith(`${STORAGE_KEY_PREFIX}:`)) {
      window.localStorage.removeItem(key);
    }
  }
}

export function sessionListCacheFallback(teamId: string) {
  const cached = readSessionListCacheSync(teamId);
  if (!cached) {
    return undefined;
  }

  return cached.directory?.sessions ?? cached.data;
}

export function sessionListDirectoryCacheFallback(teamId: string) {
  return readSessionListCacheSync(teamId)?.directory ?? null;
}
